import { useParams, useNavigate } from "react-router-dom";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import css from "./UpdateDishComponent.module.css";
import {
	dishesActions,
	dishProductsAction,
	dishSemiFinishedActions,
	arrayFormattedDishProducts,
	arrayFormattedDishSemiFinished,
} from "../../../store/slices/dishes-slices";
import { dishesService } from "../../../services/dishes-services";
import { DishType } from "../../../constants/enums";
import { DishRightBlockCreateModal } from "../dishesSectionModals/DishRightBlockCreateModal/DishRightBlockCreateModal";
import { DishLeftBlockCreateModal } from "../dishesSectionModals/DishLeftBlockCreateModal/DishLeftBlockCreateModal";
import { DishAddProductModal } from "../dishesSectionModals/DishAddProductModal/DishAddProductModal";
import { DishAddSemiFinishModal } from "../dishesSectionModals/DishAddSemiFinishModal/DishAddSemiFinishModal";

const NUTRITION_OVERRIDE_DEFAULTS = {
	energyOverridden: false,
	proteinsOverridden: false,
	fatsOverridden: false,
	carbohydratesOverridden: false,
	costOverridden: false,
};

function UpdateDishComponent({ dishIdData, isModal = false, setIsOpenUpdate }) {
	const { id } = useParams();
	const dishId = id || dishIdData;

	const navigate = useNavigate();

	const [dishProductAddOpen, setDishProductAddOpen] = useState(false);
	const [dishSemiFinishedAddOpen, setDishSemiFinishedAddOpen] = useState(false);
	const [uploadedImage, setUploadedImage] = useState(null);
	const [selectedCategory, setSelectedCategory] = useState([]);
	const [completeDishData, setCompleteDishData] = useState(null);
	const [weight, setWeight] = useState(0);
	const [weightNetto, setWeightNetto] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Track if this is the initial load to prevent overwriting DB values
	const isInitialLoad = useRef(true);

	const dispatch = useDispatch();
	const { t } = useTranslation();

	const {
		trigger: dishProductsTrigger,
		dishProductsWeight,
		dishProductsWeightNetto,
		dishProductsCost,
		dishProductEnergy,
		dishProductsFats,
		dishProductsProteins,
		dishProductsCarbohydrates,
	} = useSelector((state) => state.dishProducts);

	const {
		trigger: dishSemiFinishedTrigger,
		dishSemiFinishedWeight,
		dishSemiFinishedWeightNetto,
		dishSemiFinishedCost,
		dishSemiFinishedEnergy,
		dishSemiFinishedFats,
		dishSemiFinishedProteins,
		dishSemiFinishedCarbohydrates,
	} = useSelector((state) => state.dishSemiFinished);

	const methods = useForm({
		defaultValues: NUTRITION_OVERRIDE_DEFAULTS,
		mode: "onChange",
	});
	const { reset, setValue, watch } = methods;

	const energyOverridden = watch("energyOverridden");
	const proteinsOverridden = watch("proteinsOverridden");
	const fatsOverridden = watch("fatsOverridden");
	const carbohydratesOverridden = watch("carbohydratesOverridden");
	const costOverridden = watch("costOverridden");

	const formattedProducts = useSelector(arrayFormattedDishProducts);
	const formattedSemiFinished = useSelector(arrayFormattedDishSemiFinished);

	// Fetch complete dish data on component mount
	useEffect(() => {
		const fetchCompleteDishData = async () => {
			try {
				setIsLoading(true);
				const response = await dishesService.getDishById(dishId);
				setCompleteDishData(response);
				setWeight(response.weight || 0);
				setWeightNetto(response.weightNetto || 0);
			} catch (error) {
				console.error(
					"DishUpdateModal - Error fetching complete dish data:",
					error,
				);
				// setCompleteDishData(null);
			} finally {
				setIsLoading(false);
			}
		};

		if (dishId) {
			fetchCompleteDishData();
		} else {
			setIsLoading(false);
		}
	}, [dishId]);

	// Initialize products and semi-finished items
	useEffect(() => {
		if (
			!isLoading &&
			completeDishData &&
			completeDishData.products &&
			completeDishData.semiFinisheds
		) {
			dispatch(dishProductsAction.updateArray(completeDishData.products));
			dispatch(
				dishSemiFinishedActions.updateArray(completeDishData.semiFinisheds),
			);
			dispatch(dishProductsAction.changeTrigger());

			// Mark initial load as complete after a short delay to allow redux updates
			setTimeout(() => {
				isInitialLoad.current = false;
			}, 100);
		}
	}, [dispatch, isLoading, completeDishData]);

	// One-time reset for fields that don't change
	useEffect(() => {
		if (!isLoading && completeDishData) {
			reset({
				nameEn: completeDishData.name.en,
				nameRu: completeDishData.name.ru,
				nameKa: completeDishData.name.ge,
				nameAr: completeDishData.name.ar,
				descriptionEn: completeDishData.description.en,
				descriptionRu: completeDishData.description.ru,
				descriptionKa: completeDishData.description.ge,
				descriptionAr: completeDishData.description.ar,
				recipeEn: completeDishData.recipe.en,
				recipeRu: completeDishData.recipe.ru,
				recipeGe: completeDishData.recipe.ge,
				recipeAr: completeDishData.recipe.ar,
				price: completeDishData.price,
				Cost: completeDishData.cost || 0,
				Energy: completeDishData.energy || 0,
				Fats: completeDishData.fats || 0,
				Proteins: completeDishData.proteins || 0,
				Carbohydrates: completeDishData.carbs || 0,
				type: completeDishData.type || DishType.REGULAR,
				energyOverridden: completeDishData.energyOverridden ?? false,
				proteinsOverridden: completeDishData.proteinsOverridden ?? false,
				fatsOverridden: completeDishData.fatsOverridden ?? false,
				carbohydratesOverridden:
					completeDishData.carbohydratesOverridden ?? false,
				costOverridden: completeDishData.costOverridden ?? false,
			});
			// Handle categories as an array, fallback to empty array if not present
			const categories = completeDishData.categories.map(
				(category) => category.id,
			);
			setSelectedCategory(categories);
		}
	}, [reset, completeDishData, isLoading]);

	// Only recalculate values when ingredients change, not on initial load
	useEffect(() => {
		if (!isInitialLoad.current) {
			const totalWeight = dishProductsWeight + dishSemiFinishedWeight;
			const totalWeightNetto =
				dishProductsWeightNetto + dishSemiFinishedWeightNetto;
			const totalCost = dishProductsCost + dishSemiFinishedCost;
			const totalEnergy = dishProductEnergy + dishSemiFinishedEnergy;
			const totalFats = dishProductsFats + dishSemiFinishedFats;
			const totalProteins = dishProductsProteins + dishSemiFinishedProteins;
			const totalCarbs =
				dishProductsCarbohydrates + dishSemiFinishedCarbohydrates;

			setValue("weight", totalWeight);
			setValue("weightNet", totalWeightNetto);
			if (!costOverridden) setValue("Cost", totalCost);
			if (!energyOverridden) setValue("Energy", totalEnergy);
			if (!fatsOverridden) setValue("Fats", totalFats);
			if (!proteinsOverridden) setValue("Proteins", totalProteins);
			if (!carbohydratesOverridden) setValue("Carbohydrates", totalCarbs);
			setValue("type", completeDishData.type || DishType.REGULAR);
			setWeight(totalWeight);
			setWeightNetto(totalWeightNetto);
		}
	}, [
		dishProductsTrigger,
		dishSemiFinishedTrigger,
		dishProductsWeight,
		dishSemiFinishedWeight,
		dishProductsWeightNetto,
		dishSemiFinishedWeightNetto,
		dishProductsCost,
		dishSemiFinishedCost,
		dishProductEnergy,
		dishSemiFinishedEnergy,
		dishProductsFats,
		dishSemiFinishedFats,
		dishProductsProteins,
		dishSemiFinishedProteins,
		dishProductsCarbohydrates,
		dishSemiFinishedCarbohydrates,
		setValue,
	]);

	// When a checkbox is unticked, immediately recalculate that field from ingredients
	useEffect(() => {
		if (isInitialLoad.current) return;
		if (!energyOverridden) {
			setValue("Energy", dishProductEnergy + dishSemiFinishedEnergy);
		}
	}, [energyOverridden]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isInitialLoad.current) return;
		if (!proteinsOverridden) {
			setValue("Proteins", dishProductsProteins + dishSemiFinishedProteins);
		}
	}, [proteinsOverridden]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isInitialLoad.current) return;
		if (!fatsOverridden) {
			setValue("Fats", dishProductsFats + dishSemiFinishedFats);
		}
	}, [fatsOverridden]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isInitialLoad.current) return;
		if (!carbohydratesOverridden) {
			setValue(
				"Carbohydrates",
				dishProductsCarbohydrates + dishSemiFinishedCarbohydrates,
			);
		}
	}, [carbohydratesOverridden]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isInitialLoad.current) return;
		if (!costOverridden) {
			setValue("Cost", dishProductsCost + dishSemiFinishedCost);
		}
	}, [costOverridden]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleCategoryChange = (value) => {
		// value is already an array from Ant Design Select
		setSelectedCategory(value);
		setValue("categories", value);
	};

	const onSubmit = async (data) => {
		try {
			setIsSubmitting(true);
			// const totalWeight = dishProductsWeight + dishSemiFinishedWeight;
			// const totalWeightNetto =
			//   dishProductsWeightNetto + dishSemiFinishedWeightNetto;
			// const totalCost = dishProductsCost + dishSemiFinishedCost;
			// const totalEnergy = dishProductEnergy + dishSemiFinishedEnergy;
			// const totalFats = dishProductsFats + dishSemiFinishedFats;
			// const totalProteins = dishProductsProteins + dishSemiFinishedProteins;
			// const totalCarbs =
			//   dishProductsCarbohydrates + dishSemiFinishedCarbohydrates;

			// Validate and sanitize products and semiFinisheds data
			const validProducts = Array.isArray(formattedProducts)
				? formattedProducts.filter((p) => p && p.productId)
				: [];
			const validSemiFinisheds = Array.isArray(formattedSemiFinished)
				? formattedSemiFinished.filter((sf) => sf && sf.semiFinishedId)
				: [];

			const formData = new FormData();
			if (uploadedImage) {
				formData.append("file", uploadedImage);
			}

			formData.append(
				"name",
				JSON.stringify({
					en: data.nameEn,
					ru: data.nameRu,
					ge: data.nameKa,
					ar: data.nameAr,
				}),
			);

			formData.append(
				"description",
				JSON.stringify({
					en: data.descriptionEn,
					ru: data.descriptionRu,
					ge: data.descriptionKa,
					ar: data.descriptionAr,
				}),
			);

			formData.append(
				"recipe",
				JSON.stringify({
					en: data.recipeEn,
					ru: data.recipeRu,
					ge: data.recipeGe,
					ar: data.recipeAr,
				}),
			);

			formData.append("img", completeDishData.img);
			formData.append("cost", Number(data.Cost) || 0);
			formData.append("price", parseFloat(data.price) || 0);
			if (data.weight >= 0) {
				formData.append("weight", data.weight);
			}
			if (data.weightNet >= 0) {
				formData.append("weightNetto", data.weightNet);
			}
			formData.append("categories", JSON.stringify(selectedCategory || []));
			formData.append("products", JSON.stringify(validProducts));
			formData.append("semiFinisheds", JSON.stringify(validSemiFinisheds));
			formData.append("type", data.type || DishType.REGULAR);

			if (data.energyOverridden) {
				formData.append("energy", data.Energy);
			}
			if (data.proteinsOverridden) {
				formData.append("proteins", data.Proteins);
			}
			if (data.fatsOverridden) {
				formData.append("fats", data.Fats);
			}
			if (data.carbohydratesOverridden) {
				formData.append("carbohydrates", data.Carbohydrates);
			}

			formData.append(
				"energyOverridden",
				data.energyOverridden ? "true" : "false",
			);
			formData.append(
				"proteinsOverridden",
				data.proteinsOverridden ? "true" : "false",
			);
			formData.append("fatsOverridden", data.fatsOverridden ? "true" : "false");
			formData.append(
				"carbohydratesOverridden",
				data.carbohydratesOverridden ? "true" : "false",
			);
			formData.append("costOverridden", data.costOverridden ? "true" : "false");

			await dishesService.updateDishesById(dishId, formData);
			dispatch(dishesActions.changeTrigger());
			toast.success(t("Dish updated successfully!"));
		} catch (e) {
			console.error("DishUpdateModal - Error updating dish:", e);
			toast.error(t("modal-toast-creating-error"));
		} finally {
			setIsSubmitting(false);
		}
	};
	return (
		<div className={`${css.wrapper} ${isModal ? css.modal : ""}`}>
			<h1 className={css.title}>Update Dish</h1>
			{isLoading || !completeDishData ? (
				<div className={css.loadingContainer}>
					<div>{t("Loading...")}</div>
				</div>
			) : (
				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit(onSubmit)} className={css.form}>
						<div className={css.infoBlock}>
							<div className={css.leftBlock}>
								<DishLeftBlockCreateModal
									setDishProductAddOpen={setDishProductAddOpen}
									setDishSemiFinishedAddOpen={setDishSemiFinishedAddOpen}
									setUploadedImage={setUploadedImage}
									weight={weight}
									weightNetto={weightNetto}
									initialImage={completeDishData?.img}
									dishId={dishId}
								/>
							</div>

							<div className={css.rightBlock}>
								<DishRightBlockCreateModal
									selectedCategory={selectedCategory}
									setSelectedCategory={setSelectedCategory}
									handleCategoryChange={handleCategoryChange}
								/>
							</div>
						</div>

						<div className={css.buttonsBlock}>
							<button
								type="button"
								className={css.cancelButton}
								onClick={() => {
									if (isModal === true) {
										setIsOpenUpdate(false);
									} else {
										navigate(-1);
									}
								}}
							>
								{t("modal-cancel")}
							</button>
							<button type="submit" className={css.submitButton} disabled={isSubmitting}>
								{isSubmitting ? (
									<>
										<span className={css.spinner} />
										{t("Updating...")}
									</>
								) : (
									t("Update")
								)}
							</button>
						</div>
					</form>
				</FormProvider>
			)}

			{!isLoading && dishProductAddOpen && (
				<DishAddProductModal setDishProductAddOpen={setDishProductAddOpen} />
			)}

			{!isLoading && dishSemiFinishedAddOpen && (
				<DishAddSemiFinishModal
					setDishSemiFinishedAddOpen={setDishSemiFinishedAddOpen}
				/>
			)}
		</div>
	);
}

export default UpdateDishComponent;
